import { type Extension } from '@atlassian/forge-ui';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { containsNetworkError } from '@confluence/network';

import type { ForgeModules } from '../ForgeModules.ts';

import { type UseExtensionListProps } from './useExtensionList.tsx';
import { preloadForgeApps } from './preloadForgeApps.ts';

const setWindowExtensions = (
	moduleType: string,
	extensions: Extension[],
	error: Error | undefined,
) => {
	window.__SSR_FORGE_EXTENSIONS__ = window.__SSR_FORGE_EXTENSIONS__ || {};
	window.__SSR_FORGE_EXTENSIONS__[moduleType] = {
		extensions,
		error,
	};
};

export async function preloadUseExtensionList({
	contentId,
	spaceKey,
	moduleType,
	includeHidden = false,
}: UseExtensionListProps & {
	contentId?: string;
	spaceKey: string;
	moduleType: ForgeModules;
	includeHidden?: boolean;
}) {
	const { cloudId, locale } = getPreloaderFnContext();

	return SSRMeasures.run('ssr-app/prepare/preloadQuery/fetch:ForgeExtensionList', async () => {
		try {
			const extensions = await preloadForgeApps({
				cloudId,
				locale,
				types: [moduleType],
				context: {
					spaceKey,
					contentId: contentId != null ? Number(contentId) : undefined,
				},
				includeHidden,
			});

			if (extensions) {
				setWindowExtensions(moduleType, extensions, undefined);
			}
		} catch (e) {
			// Want to handle network errors to not affect experience SLA
			if (containsNetworkError(e)) {
				markErrorAsHandled(e);
			} else {
				ssrPrepareErrorsClient.submitError(
					new Error(`Filed to get forge extension list: ${e.message}`),
				);
			}
		}
	});
}
